import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import ContextMenuWrapper from './components/ContextMenuWrapper';
import DashboardLayout from './components/DashboardLayout';
import RequireAuth from './components/RequireAuth';
import Home from './pages/Home';
import Inventory from './pages/Inventory';
import PrintPurchaseOrderPage from "./pages/PrintPurchaseOrderPage";
import PrintPurchaseOrderPageShop from "./pages/PrintPurchaseOrderPageShop";
import UploadExcel from "./pages/UploadExcel";
import Scrape from "./pages/Scrape";
import ScrapeSingle from "./pages/ScrapeSingle";
import StockIn from "./pages/StockIn";
import StockInMobile from "./pages/StockInMobile";
import RegisterPage from "./pages/RegisterPage";
import PurchaseOrderSearch from "./pages/PurchaseOrderSearch";
import InventoryDetail from "./components/InventoryDetail";
import PurchaseOrderPage from "./pages/PurchaseOrderPage";
import TransactionHistoryList from './pages/TransactionHistoryList';
import TransactionHistoryDetail from './pages/TransactionHistoryDetail';
import BackendTaskPanel from "./pages/BackendTaskPanel";
import JournalPrintView from './components/JournalPrintView';
import SettlementPrintView from './components/SettlementPrintView';
import LoginForm from "./pages/LoginForm";
import PicPrintPreview from './components/PicPrintPreview';
import BuyPrintPreview from './components/BuyPrintPreview';
import MailPage from "./pages/MailPage";
import SearchForm from "./components/SearchForm";
import useInventoryViewStore from "./stores/useInventoryViewStore";
import SalesSummaryPage from "./pages/SalesSummaryPage";
import SalesRegisterSummaryPage from './pages/SalesRegisterSummaryPage';
import InventoryCleanupPage from './pages/InventoryCleanupPage';
import DailySalesPage from "./pages/DailySalesPage";
import InventoryDiff from "./pages/InventoryDiff";
import ShiftManagementPage from "./pages/ShiftManagementPage";
import ShiftRequestOffPage from "./pages/ShiftRequestOffPage";
import ShiftStaffMasterPage from "./pages/ShiftStaffMasterPage";
import ShiftLocationMasterPage from "./pages/ShiftLocationMasterPage";
import PrinterSettingsPage from "./pages/PrinterSettingsPage";
import CustomerDisplayPage from "./pages/CustomerDisplayPage";
import TaxExemptReportPage from "./pages/TaxExemptReportPage";
import WebTabsPage from "./pages/WebTabsPage";
import DailyCashReportPage from "./pages/DailyCashReportPage";
import { printLabelAndDeleteById, addLabelAndPrintQr, addLabelAndPrintQr_special} from "./components/PriceLabels"; 
import { apiRequest } from "./utils/api";
import { useAuthContext } from "./context/AuthContext"; 
import { useAlert } from './components/AlertModal';

const AppRoutes = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const toggleShowPriceLabel = useInventoryViewStore((s) => s.toggleShowPriceLabel);
  const actualShowPriceLabel = useInventoryViewStore((s) => s.actualShowPriceLabel);
  const { user: userInfo} = useAuthContext();
  const { showAlert } = useAlert(); 

  // ✅ ユーザー情報を表示
  console.log("🔍 現在のユーザー情報:", userInfo);
  

  // 📌 ページ別にスクロール対象クラス名を返す
  const getScrollTargetSelector = () => {
    if (location.pathname.startsWith("/inventory_list")) {
      return ".inventory_container_scroll";
    } else if (location.pathname.startsWith("/inventory_list")) {
      return ".labeltable-container";
    } else if (location.pathname.startsWith("/inventory_detail")) {
      return ".product-detail-container-scroll";  
    } else if (location.pathname.startsWith("/SalesSummaryPage")) {
      return ".SalesSummaryPage-listbox";   
    } else {
      return "body";
    }
  };

  const getMenuItems = (id) => {
    const items = [];

    if (location.pathname.startsWith("/inventory_list")) {
      // inventory_list のときは全て表示
      items.push({
        label: actualShowPriceLabel ? "🔍 検索表示" : "🏷 ラベル表示",
        onClick: () => toggleShowPriceLabel(),
      });
    
      if (!actualShowPriceLabel) {
        items.push({
          label: "⚡ QR印刷",
          onClick: async () => {
            if (!id) {
              showAlert("⚠️ 商品IDが取得できませんでした。");
              return;
            }
            await addLabelAndPrintQr(id, userInfo?.store_id, showAlert);
          },
        });
        items.push({
          label: "⚡ QR特価",
          onClick: async () => {
            if (!id) {
              showAlert("⚠️ 商品IDが取得できませんでした。");
              return;
            }
            await addLabelAndPrintQr_special(id, userInfo?.store_id, showAlert);
          },
        });
        items.push({
          label: "⚡ JAN印刷",
          onClick: async () => {
            if (!id) {
              showAlert("⚠️ 商品IDが取得できませんでした。");
              return;
            }
            const storeId = userInfo?.store_id;
            try {
              const item = await apiRequest(`inventory/inventory/${id}/`, "GET");
              const price = item.store_inventory_set?.find(inv => inv.store === storeId)?.sell_price ?? 0;
              await printLabelAndDeleteById(item, price, showAlert);
            } catch (error) {
              console.error("JAN印刷エラー:", error);
              showAlert("印刷に失敗しました");
            }
          },
        });
      }
    
      return items;
    }
    
    if (location.pathname.startsWith("/SalesSummaryPage")) {
      // SalesSummaryPage のときは印刷ボタンだけ表示
      items.push({
        label: "⚡ QR印刷",
        onClick: async () => {
          if (!id) {
            showAlert("⚠️ 商品IDが取得できませんでした。");
            return;
          }
          await addLabelAndPrintQr(id, userInfo?.store_id, showAlert);
        },
      });
      items.push({
        label: "⚡ JAN印刷",
        onClick: async () => {
          if (!id) {
            showAlert("⚠️ 商品IDが取得できませんでした。");
            return;
          }
          const storeId = userInfo?.store_id;
          try {
            const item = await apiRequest(`inventory/inventory/${id}/`, "GET");
            const price = item.store_inventory_set?.find(inv => inv.store === storeId)?.sell_price ?? 0;
            await printLabelAndDeleteById(item, price, showAlert);
          } catch (error) {
            console.error("JAN印刷エラー:", error);
            showAlert("印刷に失敗しました");
          }
        },
      });
    
      return items;
    }

    // メールページ
    if (location.pathname.startsWith("/mail")) {
      return [{ label: "📩 メール更新", onClick: () => console.log("更新") }];
    }

    // その他
    return [{ label: "🏠 ホーム", onClick: () => navigate("/") }];
  };

  return (
    <ContextMenuWrapper
      menuItems={getMenuItems}
      scrollTargetSelector={getScrollTargetSelector()}
    >
      <Routes>
        {/* 非ログインルート */}
        <Route path="/login" element={<LoginForm />} />
        <Route path="/inventory_list2" element={<SearchForm />} />

        {/* 認証付きルート */}
        <Route path="/" element={<RequireAuth><DashboardLayout><Home /></DashboardLayout></RequireAuth>} />
        <Route path="/inventory_list" element={<RequireAuth><DashboardLayout><Inventory /></DashboardLayout></RequireAuth>} />
        <Route path="/print-purchase-order/:id" element={<RequireAuth><PrintPurchaseOrderPage /></RequireAuth>} />
        <Route path="/print-purchase-order-shop/:id" element={<RequireAuth><PrintPurchaseOrderPageShop /></RequireAuth>}/>
        <Route path="/upload_excel" element={<RequireAuth><DashboardLayout><UploadExcel /></DashboardLayout></RequireAuth>} />
        <Route path="/scrape_all" element={<RequireAuth><DashboardLayout><Scrape /></DashboardLayout></RequireAuth>} />
        <Route path="/scrape_single" element={<RequireAuth><DashboardLayout><ScrapeSingle /></DashboardLayout></RequireAuth>} />
        <Route path="/stock_in" element={<RequireAuth><DashboardLayout><StockIn /></DashboardLayout></RequireAuth>} />
        <Route path="/stock_in_M/:sessionId" element={<RequireAuth><StockInMobile /></RequireAuth>} />
        <Route path="/purchase_orderpage" element={<RequireAuth><DashboardLayout><PurchaseOrderPage /></DashboardLayout></RequireAuth>} />
        <Route path="/purchase_orderpage/:id" element={<RequireAuth><DashboardLayout><PurchaseOrderPage /></DashboardLayout></RequireAuth>} />
        <Route path="/PurchaseOrderSearch" element={<RequireAuth><DashboardLayout><PurchaseOrderSearch /></DashboardLayout></RequireAuth>} />
        <Route path="/Inventory_detail/:id/:storeid" element={<RequireAuth><DashboardLayout><InventoryDetail /></DashboardLayout></RequireAuth>} />
        <Route path="/RegisterPage" element={<RequireAuth><DashboardLayout><RegisterPage /></DashboardLayout></RequireAuth>} />
        <Route path="/transaction_history" element={<RequireAuth><TransactionHistoryList /></RequireAuth>} />
        <Route path="/transaction_history/:receiptId" element={<RequireAuth><TransactionHistoryDetail /></RequireAuth>} />
        <Route path="/journal_print_view" element={<RequireAuth><JournalPrintView /></RequireAuth>} />
        <Route path="/settlement_print_view" element={<RequireAuth><SettlementPrintView /></RequireAuth>} />
        <Route path="/daily-cash-report" element={<RequireAuth><DashboardLayout><DailyCashReportPage /></DashboardLayout></RequireAuth>} />
        <Route path="/picprint_preview" element={<RequireAuth><PicPrintPreview /></RequireAuth>} />
        <Route path="/buypicprint_preview" element={<RequireAuth><BuyPrintPreview /></RequireAuth>} />
        <Route path="/Mail" element={<RequireAuth><DashboardLayout><MailPage /></DashboardLayout></RequireAuth>} />
        <Route path="/web" element={<RequireAuth><DashboardLayout><WebTabsPage /></DashboardLayout></RequireAuth>} />
        <Route path="/backendtaskpanel" element={<RequireAuth><DashboardLayout><BackendTaskPanel /></DashboardLayout></RequireAuth>} />
        <Route path="/SalesSummaryPage" element={<RequireAuth><DashboardLayout><SalesSummaryPage /></DashboardLayout></RequireAuth>} />
        <Route path="/sales_summary_by_register" element={<RequireAuth><DashboardLayout><SalesRegisterSummaryPage /></DashboardLayout></RequireAuth>} />
        <Route path="/inventory_cleanup" element={<RequireAuth><DashboardLayout><InventoryCleanupPage /></DashboardLayout></RequireAuth>} />
        <Route path="/daily_sales" element={<RequireAuth><DashboardLayout><DailySalesPage /></DashboardLayout></RequireAuth>} />
        <Route path="/inventory_diff/:sessionId" element={<RequireAuth><DashboardLayout><InventoryDiff /></DashboardLayout></RequireAuth>} />
        <Route path="/shift_request_off" element={<RequireAuth><DashboardLayout><ShiftRequestOffPage /></DashboardLayout></RequireAuth>} />
        <Route path="/shift_management" element={<RequireAuth><DashboardLayout><ShiftManagementPage /></DashboardLayout></RequireAuth>}/>
        <Route path="/shift_staff_master" element={<RequireAuth><DashboardLayout><ShiftStaffMasterPage /></DashboardLayout></RequireAuth>}/>
        <Route path="/shift_location_master" element={<RequireAuth><DashboardLayout><ShiftLocationMasterPage /></DashboardLayout></RequireAuth>}/>
        <Route path="/printer_settings" element={<RequireAuth><DashboardLayout><PrinterSettingsPage /></DashboardLayout></RequireAuth>} />
        <Route path="/customer-display" element={<CustomerDisplayPage />} />
        <Route path="/tax_exempt_report" element={<RequireAuth><DashboardLayout><TaxExemptReportPage /></DashboardLayout></RequireAuth>} />
      </Routes>
    </ContextMenuWrapper>
  );
};

export default AppRoutes;
